import React, {useState, useEffect} from 'react'
import {Helmet, HelmetProvider} from 'react-helmet-async'
import {confirmAlert} from 'react-confirm-alert'
import {useForm} from 'react-hook-form'
import useMachinesHook from '../../../api/machines'
import {ViewMachines} from '../../../components/admin/machines/ViewMachines';
import {FormMachines} from '../../../components/admin/machines/FormMachines';
import useProductsHook from '../../../api/products';
import { useMessage } from '../../../context/AdminMessageContext';
import {
    Spinner,
    Pagination,
    Message,
    Confirm,
    Search,
} from '../../../components'
import DefaultMachinesIcon from "../../../media/vending-machine.svg";
import {useTranslation} from "react-i18next";
import dynamicAPI from '../../../api/dynamicAPI';
const Machines = () => {
    const [page, setPage] = useState(1);
    const [id, setId] = useState(null);
    const [edit, setEdit] = useState(false);
    const [q, setQ] = useState('');
    const [triggerReset, setTriggerReset] = useState(false);
    const { t} = useTranslation(); // Use i18n instance to access current language
    const [productMachineId, setProductMachineId] = useState(null);
    const {
        getMachines,
        postMachine,
        updateMachine,
        deleteMachine,
        getInventoryInfoById
    } = useMachinesHook({
        page,
        q,
    });
    const {getProductsForMachine} = useProductsHook(
        {limit: 100000, page: 1, machineId : productMachineId}
    );
    const {
        data: dataProducts,
        // refetch: refetchProducts
    } = getProductsForMachine;
    const { setErrorMessage } = useMessage();
    const {
        register ,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: {
            errors
    }} = useForm({
        defaultValues: {confirmed: false, blocked: false, isTerminal: false, isCoffee: false},
    });

    const {
        data,
        isLoading,
        isError,
        error,
        refetch
    } = getMachines;

    const {mutateAsync: mutateAsyncUpdate} = updateMachine;
    const {mutateAsync: mutateAsyncDelete} = deleteMachine;
    const {mutateAsync: mutateAsyncPost} = postMachine;

    const formCleanHandler = () => {
            setEdit(false);
            setId(null);
            reset(); // Reset the form
            setTriggerReset(prev => !prev);
            setValue('items', '');
    };

    // Refetch data when the search query changes
    // Refetch data when the page changes
    useEffect(() => {
        if (q.trim() || page) {
            refetch();
        }
    }, [page, q, refetch]);

    const searchHandler = (e) => {
        e.preventDefault();
        refetch();
        setPage(1); // Reset to the first page after a search
    };

    const deleteHandler = (id) => {
        confirmAlert(Confirm(() => mutateAsyncDelete(id))); // Confirm and then delete
    };

    const submitHandler = async (formData) => {
        /* ---------- 1. Parse & validate items ---------- */
        let parsedItems = [];
        try {
            parsedItems = formData.items ? JSON.parse(formData.items) : [];
        } catch (err) {
            console.log('Items JSON is malformed.');
            setErrorMessage('Items JSON is malformed.');
            return;
        }

        if (edit && parsedItems.length === 0) {
            console.log('Planogram is empty, add at least one item before saving.');
            setErrorMessage('Planogram is empty, add at least one item before saving.');
            return;
        }

        /* ---------- 2. Build request payload ---------- */
        const basePayload = {
            kiosk_external_id         : formData.kiosk_external_id,
            machine_name              : formData.machine_name,
            machine_phone             : formData.machine_phone,
            items                     : parsedItems,
            items_errors              : formData.items_errors       ? JSON.parse(formData.items_errors)        : [],
            pre_items                 : formData.pre_items          ? JSON.parse(formData.pre_items)           : [],
            post_items                : formData.post_items         ? JSON.parse(formData.post_items)          : [],
            pre_items_errors          : formData.pre_items_errors   ? JSON.parse(formData.pre_items_errors)    : [],
            post_items_errors         : formData.post_items_errors  ? JSON.parse(formData.post_items_errors)   : [],
            machineType               : formData.machineType,
            qr_code_receipt           : formData.qr_code_receipt,
            machineToAdminInventory   : formData.machineToAdminInventory,
            machineToAdminPlanogram   : formData.machineToAdminPlanogram,
            enablePriceRules          : formData.enablePriceRules,
            ageRestrictions           : formData.ageRestrictions,
            payment_options           : formData.payment_options,
            machine_status            : formData.machine_status,
            machine_order_id_processing: formData.machine_order_id_processing,
            machine_coffee_api_key    : formData.machine_coffee_api_key,
            machine_coffee_api_secret : formData.machine_coffee_api_secret,
            coffee_orders_sync_from : formData.coffee_orders_sync_from,
            isCoffee                  : formData.isCoffee,
            external_md5_key          : formData.external_md5_key,
            erp_id                    : formData.erp_id,
            erp_token                 : formData.erp_token,
            erp_doc_series            : formData.erp_doc_series,
            confirmed                 : formData.confirmed,
            isTerminal                : formData.isTerminal
            // blocked                 : formData.blocked,   ← keep commented if unused
        };
        /* ---------- 3. Fire the correct mutation ---------- */
        try {
            if (edit) {
                await mutateAsyncUpdate({ _id: id, ...basePayload });
            } else {
                const created = await mutateAsyncPost(basePayload);
                const newId = created?.data?._id || created?._id;
                if (newId) {
                    setId(newId);
                    setEdit(true);
                    setProductMachineId(newId); // triggers products query
                    // optionally refetch machines list too
                    refetch();
                }
            }
        } catch (err) {
            console.log(err.message || 'Request failed.');
            setErrorMessage(err.message || 'Request failed.');
        }
    };


    // const fetchTotalMachineStock = async (machineId) => {
    //     // Assuming you have a function similar to `dynamicAPI` for fetching
    //     try {
    //         const response = await dynamicAPI('get', `${url}/${machineId}/totalStock`, {});
    //         alert(`Total stock for machine ${machineId}: ${response.totalCurrentStock}`);
    //     } catch (error) {
    //         console.error("Failed to fetch total stock for machine", machineId, error);
    //         alert("Failed to fetch total stock");
    //     }
    // };
    const [isFetchingMachineLogs, setIsFetchingMachineLogs] = useState(false);

    const fetchMachineLogs = async (machine_id, code) => {
        try {
            setIsFetchingMachineLogs(true);

            const payload = { machine_id };
            if (code && String(code).trim()) payload.code = String(code).trim();

            const result = await dynamicAPI('post', `/api/auth/log-data/machine`, payload);

            // If code was provided -> backend returns JSON (filtered blocks)
            if (payload.code) {
                downloadJson(result, `machine-${machine_id}-code-${payload.code}-${Date.now()}.json`);
                return;
            }

            // If no code -> backend returns raw day logs as string
            if (result.logs) {
                downloadNdjson(result.logs, `machine-${machine_id}-logs-${Date.now()}.ndjson`);
            } else {
                alert('No valid logs received for this machine/day.');
            }
        } catch (error) {
            console.error('Failed to fetch machine logs', error);
            alert(error?.error || 'Failed to fetch machine logs');
        } finally {
            setIsFetchingMachineLogs(false);
        }
    };

    const downloadNdjson = (logsContent, filename) => {
        const blob = new Blob([logsContent], { type: 'application/x-ndjson' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const downloadJson = (obj, filename) => {
        const jsonStr = JSON.stringify(obj, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };


    const editHandler = (machine) => {
        formCleanHandler();
        setId(machine._id);
        setEdit(true); // Set editing mode

        setProductMachineId(machine._id);    // this turns the query for fetching “on”
        // refetchProducts();                   // fetching products for the current machine

        setValue('kiosk_external_id', machine.kiosk_external_id);
        setValue('machine_name', machine.machine_name);
        setValue('machine_phone', machine.machine_phone);
        setValue('items', machine.items ? JSON.stringify(machine.items) : '');
        setValue('items_errors', machine.items_errors ? JSON.stringify(machine.items_errors) : '');
        setValue('pre_items', machine.pre_items ? JSON.stringify(machine.pre_items) : '');
        setValue('post_items', machine.post_items ? JSON.stringify(machine.post_items) : '');
        setValue('pre_items_errors', machine.pre_items_errors ? JSON.stringify(machine.pre_items_errors) : '');
        setValue('post_items_errors', machine.post_items_errors ? JSON.stringify(machine.post_items_errors) : '');
        setValue('machine_order_id_processing', machine.machine_order_id_processing);
        setValue('coins_total_stock', machine.coins_total_stock);
        setValue('bills_stock', machine.bills_stock ? JSON.stringify(machine.bills_stock) : '');
        setValue('erp_id', machine.erp_id);
        setValue('erp_token', machine.erp_token);
        setValue('erp_doc_series', machine.erp_doc_series);
        setValue('machine_status', machine.machine_status);
        setValue('machineType', machine.machineType);
        setValue('qr_code_receipt', machine.qr_code_receipt);
        setValue('machineToAdminInventory', machine.machineToAdminInventory);
        setValue('machineToAdminPlanogram', machine.machineToAdminPlanogram);
        setValue('machine_coffee_api_key', machine.machine_coffee_api_key);
        setValue('machine_coffee_api_secret', machine.machine_coffee_api_secret);
        setValue('coffee_orders_sync_from', machine.coffee_orders_sync_from);
        setValue('isCoffee', machine.isCoffee);
        setValue('external_md5_key', machine.external_md5_key);
        setValue('enablePriceRules', machine.enablePriceRules);
        setValue('ageRestrictions', machine.ageRestrictions);
        setValue('payment_options', machine.payment_options);
        setValue('confirmed', machine.confirmed);
        setValue('isTerminal', machine.isTerminal);
        // setValue('blocked', machine.blocked);
    }

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>Machines</title>
                    <meta property="og:title" content="Machines" key="title"/>
                </Helmet>
            </HelmetProvider>
            <div className='view-list-top-bar'>

                <h3 className='fw-light text-muted text-center mb-3'>
                    {t("Machines List")} <sup className='fs-6'> [{data && data.total}] </sup>
                    <img className="machines-list-title-icon admin-list-title-icon" src={DefaultMachinesIcon}
                         alt="Default Marker Icon"/>
                </h3>

                <div className='d-flex align-items-center justify-content-between mb-2'>
                    <button
                        className='btn add-new-entry-button align-self-end'
                        data-bs-toggle='modal'
                        data-bs-target='#machineModal'
                        onClick={() => formCleanHandler()}
                    >
                        {t("Add New Machine")}
                    </button>
                    <Pagination data={data} setPage={setPage}/>
                </div>

                <div className='col-auto full-search'>
                    <Search
                        placeholder='Search by Code,Name'
                        setQ={setQ}
                        q={q}
                        searchHandler={searchHandler}
                    />
                </div>
            </div>
            {isLoading ? (
                <Spinner/>
            ) : isError ? (
                <Message variant='danger'>{error}</Message>
            ) : (
                <ViewMachines
                    data={data}
                    editHandler={editHandler}
                    deleteHandler={deleteHandler}
                    getInventoryInfoById={getInventoryInfoById}
                    setErrorMessage={setErrorMessage}
                />
            )}
            <div className='pagination-bottom-block'>
                <Pagination data={data} setPage={setPage}/>
            </div>
            <FormMachines
                edit={edit}
                formCleanHandler={formCleanHandler}
                isLoading={isLoading}
                isError={isError}
                errors={errors}
                register={register}
                handleSubmit={handleSubmit}
                submitHandler={submitHandler}
                watch={watch}
                dataProducts={dataProducts}
                dataMachines={data}
                setValue={setValue}
                triggerReset={triggerReset}
                fetchMachineLogs={fetchMachineLogs}
                isFetchingMachineLogs={isFetchingMachineLogs}
            />
        </>
    )
}
export default Machines
